import { Box, Typography, Tooltip, IconButton, Chip } from "@mui/material";
import React, { useEffect, useState } from "react";
import Lottie from "react-lottie";
import RocketImage from "../../assets/empty.json";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import ApplyIcon from "@mui/icons-material/SaveAltRounded";
import { SetView } from "../../components/SetView";
import {
  getReadableShortDate,
  getReadableShortTime,
} from "../../utils/commons";
import { ExerciseSetList } from "./ExerciseSetList";
import { WOD } from "./types";

const emptyOptions = {
  loop: true,
  autoplay: true,
  animationData: RocketImage,
};

export const WODView = ({
  wod,
  onDeleteWod,
  onCloneClicked,
}: {
  wod: WOD;
  onDeleteWod: () => void;
  onCloneClicked: () => void;
}) => {
  const [currentSetIndex, setCurrentSetIndex] = useState<number>(-1);

  useEffect(() => {
    setCurrentSetIndex(-1);
  }, [wod]);

  let exercises =
    currentSetIndex > -1 ? wod?.sets[currentSetIndex]?.exercises : [];

  const handleViewSet = (index: number): void => {
    setCurrentSetIndex(index);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        gap: 2,
        minHeight: 0,
        overflow: "hidden",
        pl: 2,
      }}
    >
      {/* Sets column */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "50%",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            pb: 1.5,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Workout Plan
            </Typography>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            >
              <Chip
                label={getReadableShortDate(wod.date)}
                size="small"
                color="secondary"
                sx={{ height: 22, fontSize: "0.75rem" }}
              />
              <Typography variant="caption" color="text.secondary">
                {getReadableShortTime(Number(wod.duration || 0))}
              </Typography>
            </Box>
          </Box>
          {wod.sets.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Use for today">
                <IconButton
                  onClick={() => onCloneClicked()}
                  size="small"
                  color="primary"
                >
                  <ApplyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete workout">
                <IconButton
                  onClick={onDeleteWod}
                  size="small"
                  sx={{
                    color: "error.main",
                    "&:hover": { bgcolor: "rgba(244,67,54,0.08)" },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            scrollbarWidth: "thin",
          }}
        >
          {wod.sets.length > 0 ? (
            wod.sets.map((set, index) => (
              <SetView
                key={`${set.id}_${index}`}
                viewOnly
                set={set}
                isSelected={currentSetIndex === index}
                onViewExercisesClicked={() => handleViewSet(index)}
                index={index}
              />
            ))
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
              }}
            >
              <Lottie options={emptyOptions} height={80} width={80} />
              <Typography
                variant="body2"
                sx={{ textAlign: "center", mt: 1, color: "text.secondary" }}
              >
                No sets in this workout
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Exercises column */}
      {currentSetIndex !== -1 && (
        <ExerciseSetList viewOnly exercises={exercises} />
      )}
    </Box>
  );
};
